import Dropzone from 'dropzone';
import * as React from 'react';
import { Button } from 'reactstrap';
import { API, AWN_USERINFO_REQUEST_HEADER } from '../../constants';
import Icon from '../../widgets/Icons';
import './ZendeskFileUpload.scss';
import { SessionStorageKey } from '../../types';

export type AttachmentTokens = Array<string>;
export type ZendeskFileUploadStatus = {
  tokens: AttachmentTokens;
  uploadInProgress: boolean;
};

type TokenResponse = {
  token: string;
};

type TokenFile = Dropzone.DropzoneFile & {
  token?: string;
};

type Props = {
  onChange: (arg0: ZendeskFileUploadStatus) => any;
};

type State = {
  uploadInProgress: boolean;
};

export default class ZendeskFileUpload extends React.Component<Props, State> {
  dropzone: Dropzone | null = null;

  state = {
    uploadInProgress: false,
  };

  onChange = (uploadInProgress: boolean) => {
    if (this.dropzone) {
      this.setState({ uploadInProgress });
      this.props.onChange({
        // type assertion: override the default return type defined by Dropzone
        tokens: (this.dropzone.getAcceptedFiles() as TokenFile[])
          .filter((f) => f.token)
          // type assertion: token is checked by the previous filter()
          .map((f) => f.token as string),
        uploadInProgress,
      });
    }
  };

  createDropzone = () => {
    return new Dropzone('div#ticket-attachment-upload', {
      addRemoveLinks: true,
      clickable: '#attachment-upload-button',
      headers: {
        Authorization: `Bearer ${window.sessionStorage.getItem(
          SessionStorageKey.PRIMARY_TOKEN,
        )}`,
        [AWN_USERINFO_REQUEST_HEADER]:
          sessionStorage.getItem(SessionStorageKey.SECONDARY_TOKEN) || '',
      },
      url: API.ticketAttachment,
      maxFilesize: 20,
      maxFiles: 10,
      previewsContainer: '.dropzone-previews',
      previewTemplate: `<div class="dz-preview dz-file-preview">
        <div class="dz-image"><img data-dz-thumbnail/></div>
        <div class="dz-details">
          <div class="dz-size"><span data-dz-size/>
          </div>
          <div class="dz-filename"><span data-dz-name/>
          </div>
        </div>
        <div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress/></div>
        <div class="dz-success-mark">
          <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 512 512" style="fill: currentColor">
            <path d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0 48c110.532 0 200 89.451 200 200 0 110.532-89.451 200-200 200-110.532 0-200-89.451-200-200 0-110.532 89.451-200 200-200m140.204 130.267l-22.536-22.718c-4.667-4.705-12.265-4.736-16.97-.068L215.346 303.697l-59.792-60.277c-4.667-4.705-12.265-4.736-16.97-.069l-22.719 22.536c-4.705 4.667-4.736 12.265-.068 16.971l90.781 91.516c4.667 4.705 12.265 4.736 16.97.068l172.589-171.204c4.704-4.668 4.734-12.266.067-16.971z"/>
          </svg>
        </div>
        <div class="dz-error-mark">
          <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 512 512" style="fill: currentColor">
            <path d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm0 448c-110.5 0-200-89.5-200-200S145.5 56 256 56s200 89.5 200 200-89.5 200-200 200zm101.8-262.2L295.6 256l62.2 62.2c4.7 4.7 4.7 12.3 0 17l-22.6 22.6c-4.7 4.7-12.3 4.7-17 0L256 295.6l-62.2 62.2c-4.7 4.7-12.3 4.7-17 0l-22.6-22.6c-4.7-4.7-4.7-12.3 0-17l62.2-62.2-62.2-62.2c-4.7-4.7-4.7-12.3 0-17l22.6-22.6c4.7-4.7 12.3-4.7 17 0l62.2 62.2 62.2-62.2c4.7-4.7 12.3-4.7 17 0l22.6 22.6c4.7 4.7 4.7 12.3 0 17z"/>
          </svg>          
        </div>
        <div class="dz-error-message"><span data-dz-errormessage/></div>
        </div>`,

      /**
       * Override the Dropzone built-in "error message handler" with a custom one that does not leak
       * the raw server response to the UI in the event of an HTML response.
       * @param file - the File object that was uploaded
       * @param message - the server response payload
       * @param xhr - the XHR object used to try the upload
       */
      error: (file, message, xhr) => {
        let err = 'There was an error uploading the attachment.';
        if (file.previewElement) {
          file.previewElement.classList.add('dz-error');
          if (xhr) {
            // if this is an error resulting from communicating with server
            // add some defaults for 413 and 5xx error codes
            if (xhr.status === 413) {
              err = 'File contents are too large and were rejected by server.';
            }
            if (xhr.status > 499 && xhr.status < 600) {
              err = 'There was a server error uploading the attachment.';
            }
            if (typeof message === 'string') {
              try {
                message = JSON.parse(message);
              } catch (e) {
                // ignore; it wasn't a valid JSON response
              }
            }
          } else {
            // no xhr means it is a client-side error, so suitable message should be supplied
            //@ts-ignore: assuming message can only be string in this scenario
            err = message || err;
          }
          if (message && typeof message === 'object') {
            // if it is a JSON object, extract message if there is one
            //@ts-ignore: not sure where .error is coming from, this code might be wrong
            err = message.error || err;
            err = message.message || err;
          }
          // This is the same implementation as built-in error handler to set the msg in DOM

          for (let node of Array.from(
            file.previewElement.querySelectorAll('[data-dz-errormessage]'),
          )) {
            node.textContent = err;
          }
        }
      },
    });
  };

  componentDidMount() {
    if (!this.dropzone) {
      this.dropzone = this.createDropzone();

      this.dropzone.on('removedfile', (file: TokenFile) => {
        if (!file) return;
        // if we have a token already for this upload, remove it from Zendesk
        // (just us being nice, maybe more 'secure'... otherwise they expire after 3 days)

        if (file.token) {
          try {
            const headers = new Headers({
              Authorization: `Bearer ${window.sessionStorage.getItem(
                SessionStorageKey.PRIMARY_TOKEN,
              )}`,
            });

            fetch(`${API.ticketAttachment}/${file.token}`, {
              method: 'DELETE',
              headers,
            });
          } catch (e) {
            // ignore; we don't care if we weren't able to delete the file from Zendesk
          }
          this.onChange(this.state.uploadInProgress);
        }
      });

      this.dropzone.on(
        'success',
        (file: TokenFile, tokenResponse: TokenResponse) => {
          file.token = tokenResponse.token;
        },
      );

      this.dropzone.on('sending', () => {
        this.onChange(true);
      });

      this.dropzone.on('queuecomplete', () => {
        this.onChange(false);
      });
    }
  }

  componentWillUnmount() {
    if (this.dropzone) {
      this.dropzone.off();
      return this.dropzone.destroy();
    }
  }

  render() {
    return (
      <div id="ticket-attachment-upload" className="dropzone my-4">
        <p className="dz-max-files">
          Maximum of 10 attachments has been reached.
        </p>
        <p>
          <Icon.FileTypes />
        </p>
        <p className="dz-message">Drop files here</p>
        <p>or</p>
        <Button id="attachment-upload-button" color="secondary">
          Choose files to upload
        </Button>
        <p className="max-size">Maximum 10 attachments, up to 20 MB each.</p>
        <div className="d-flex flex-wrap dropzone-previews" />
      </div>
    );
  }
}
